/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */


import {formatDate} from '@angular/common';
import {HttpEventType} from '@angular/common/http';

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../common/constants';
import {AuthenticationService} from '../../../core/authentication/authentication.service';
import {SheetSelectorComponent} from '../../../core/components/sheet-selector/sheet-selector.component';
import {AutocompleteFilterComponent} from '../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {ColumnHeader} from '../../../core/models/column-header.model';
import {SheetSelectorInput} from '../../../core/models/sheet-selector-input.model';
import {WorksheetExportRequest} from '../../../core/models/worksheet-export-request.model';
import {SalesLagReport} from '../../../models/reports/sales-lag-report.model';
import {SelectableOptions} from '../../../models/selectable-options';
import {ExcelExportService} from '../../../services/excel-export.service';
import {ManageSalesLagService} from '../../../services/manage-sales-lag-service';

@Component({
    selector: 'app-admin-manage-sales-lag',
    templateUrl: './manage-sales-lag.component.html',
    styleUrls: ['./manage-sales-lag.component.scss'],
    standalone: false
})

export class ManageSalesLagComponent implements OnInit {

  constructor(public snackBar: FabMessageService,
              private readonly salesLagService: ManageSalesLagService,
              private readonly authService: AuthenticationService,
              public readonly excelService: ExcelExportService,
              private readonly dialog: MatDialog) {
  }

  pageSize: number;
  showSpinner: boolean;

  salesLagReportData: SalesLagReport[] = [];
  worksheetExportRequest: WorksheetExportRequest;
  excelSalesLagData: Map<string, SalesLagReport[]> = new Map<string, SalesLagReport[]>();
  SALES_LAG_REPORT = 'Sales Lag Report';

  file: File;
  sheets: SelectableOptions = new SelectableOptions();
  salesLagReportHeaders: ColumnHeader[] = [
    {name: 'brandfamID', label: 'BRANDFAM_ID'},
    {name: 'subregion', label: 'SUBREGION'},
    {name: 'plngMkt', label: 'PLNGMKT'},
    {name: 'mktDiv', label: 'MKTDIV'},
    {name: 'brand', label: 'BRAND'},
    {name: 'lbrand', label: 'LBRAND'},
    {name: 'arch', label: 'ARCH'},
    {name: 'prgmCode', label: 'PRGMCODE'},
    {name: 'styleCode', label: 'STYLECODE'},
    {name: 'bodyStyle', label: 'BODYSTYLE'},
    {name: 'brandFam', label: 'BRANDFAM'},
    {name: 'gseg', label: 'GSEG'},
    {name: 'propSeg', label: 'PROP_SEG'},
    {name: 'sosGap', label: 'SOS_GAP'},
    {name: 'eosGap', label: 'EOS_GAP'}];

  columnDef: ColDef[] = [
    {field: 'brandfamID', headerName: 'BRANDFAM_ID', filter: AutocompleteFilterComponent},
    {field: 'subregion', headerName: 'SUBREGION', filter: AutocompleteFilterComponent},
    {field: 'plngMkt', headerName: 'PLNGMKT', filter: AutocompleteFilterComponent},
    {field: 'mktDiv', headerName: 'MKTDIV', filter: AutocompleteFilterComponent},
    {field: 'brand', headerName: 'BRAND', filter: AutocompleteFilterComponent},
    {field: 'lbrand', headerName: 'LBRAND', filter: AutocompleteFilterComponent},
    {field: 'arch', headerName: 'ARCH', filter: AutocompleteFilterComponent},
    {field: 'prgmCode', headerName: 'PRGMCODE', filter: AutocompleteFilterComponent},
    {field: 'styleCode', headerName: 'STYLECODE', filter: AutocompleteFilterComponent},
    {field: 'bodyStyle', headerName: 'BODYSTYLE', filter: AutocompleteFilterComponent},
    {field: 'brandFam', headerName: 'BRANDFAM', filter: AutocompleteFilterComponent},
    {field: 'gseg', headerName: 'GSEG', filter: AutocompleteFilterComponent},
    {field: 'propSeg', headerName: 'PROP_SEG', filter: AutocompleteFilterComponent},
    {field: 'sosGap', headerName: 'SOS_GAP', filter: AutocompleteFilterComponent},
    {field: 'eosGap', headerName: 'EOS_GAP', filter: AutocompleteFilterComponent}];


  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };

  ngOnInit(): void {
    this.resetPage();
    this.getSalesLagReport();
  }

  /**
   * Reset the page number back to the first page and default page size
   */
  resetPage(): void {
    this.pageSize = 20;
  }

  /**
   * gets the sales lag report and displays it on the page
   */
  getSalesLagReport(): void {
    this.showSpinner = true;
    this.salesLagService.getSalesLagReport().subscribe(
      data => {
        this.salesLagReportData = data;
      },
      err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      () => {
        this.excelSalesLagData[this.sheets.selectedOptions[0]] = this.salesLagReportData;
        this.setExportRequest();
      }
    );
  }

  /**
   * saves the elements that have been changed
   * @return void
   */
  saveSalesLagReport(): void {
    this.salesLagService.updateSalesLagReport(this.salesLagReportData).subscribe({
      error: err => {
        this.snackBar.displayError(err, DISMISS_MSG);
        this.showSpinner = false;
      },
      complete: () => {
        this.setExportRequest();
        this.snackBar.displaySuccess(`Successfully saved Sales Lag Report`, DISMISS_MSG);
      }
    });

  }

  /**
   * Event handler for when an upload event is emitted. Receives the sales lag report
   * data in the event. Displays the results to the user, whether any validation error occurs
   * or any records were successfully updated/added/deleted.
   * @param event The event that is emitted by the Upload button component
   */
  upload(event: any): void {
    this.sheets.options = event.get('data');
    this.file = event.get('file');
    this.onFileUpload();
  }

  /**
   * Method run after uploading an Excel file.
   * Opens the SheetSelectorComponent, where a user can select which sheets to grab
   * from the uploaded Excel file.
   */
  onFileUpload(): void {
    const sheetSelectorInput: SheetSelectorInput = {
      sheets: this.sheets.options,
      isMultiSelect: false
    };
    this.dialog.open(SheetSelectorComponent, {
      data: sheetSelectorInput,
      disableClose: true,
      width: '250px'
    }).afterClosed().subscribe(
      data => {
        // Clear out the selected options before setting them in case the user already uploaded a file and
        // selected an option previously
        this.sheets.selectedOptions = [];
        if (data) {
          this.sheets.selectedOptions = data;
        }
      },
      error => {
        this.snackBar.displayError(error, DISMISS_MSG);
      },
      () => {
        if (this.sheets.selectedOptions.length > 0) {
          this.getUploadedData();
        }
      }
    );
  }

  /**
   * Get the sales lag report data from the Excel file that was uploaded
   */
  getUploadedData(): void {
    this.showSpinner = true;
    this.salesLagService.transform(this.file, this.sheets.selectedOptions).subscribe(
      (e: any) => {
        // Get Sales Lag Report data from the uploaded Excel file
        if (e.type === HttpEventType.Response) {
          this.excelSalesLagData = e.body;
        }
      },
      (error) => {
        if (error.status === 0) {
          this.snackBar.displayError('Error occurred. Please re-upload file.', DISMISS_MSG);
        } else {
          this.snackBar.displayError(error, DISMISS_MSG);
        }
        this.showSpinner = false;
      },
      () => {
        this.salesLagReportData = [...this.excelSalesLagData[this.sheets.selectedOptions[0]]];
        this.saveSalesLagReport();
      });
  }

  /**
   * Helper function used to set the export request
   */
  setExportRequest(): void {
    const excelData = new Map<string, SalesLagReport[]>();
    const numTypeFields = new Map<string, string[]>();
    numTypeFields[this.SALES_LAG_REPORT] = ['sosGap', 'eosGap'];
    excelData[this.SALES_LAG_REPORT] = this.excelSalesLagData[this.sheets.selectedOptions[0]];
    const currDate = formatDate(new Date(), 'yyyy-MM-dd', 'en');
    this.worksheetExportRequest = this.excelService.createExportRequestBody('SalesLags_' + currDate,
      excelData,
      [[this.SALES_LAG_REPORT, this.salesLagReportHeaders]],
      numTypeFields);
    this.showSpinner = false;
  }
}
